import { getCurrentScope, onScopeDispose, ref, type Ref } from "vue";

type Interval = ReturnType<typeof requestAnimationFrame>;

type RunnerFunc = (ctx: UseAnimationFrameContext) => void;

export type UseAnimationFrameContext = {
  delta: number;
  time: number;
  elapsed: number;
  stop: () => void;
};

export type UseAnimationFrame = {
  stop: () => void;
  run: (fun: RunnerFunc) => () => void;
  running: Ref<boolean>;
};

export const useAnimationFrame = (): UseAnimationFrame => {
  const id = ref<Interval | null>(null);
  const timeStarted = ref<number>(0);
  const prevTime = ref<number>(0);
  const running = ref<boolean>(false);
  const stopped = ref<boolean>(false);


  const cancel = (): void => {
    const inter = id.value;
    if (inter === null) return;
    cancelAnimationFrame(inter);
    id.value = null;
    running.value = false;
    stopped.value = false;
  }

  const stop = (): void => {
    const inter = id.value;
    if (inter === null) return;
    cancelAnimationFrame(inter);
    id.value = null;
    running.value = false;
    stopped.value = true;
  };

  const run = (fun: RunnerFunc): (() => void) => {
    if (running.value) {
      cancel();
    }
    const loop = (time: number) => {
      const prev = prevTime.value;
      const delta = time - prev;
      prevTime.value = time;
      if (!running.value) {
        timeStarted.value = time;
        running.value = true;
      }
      const elapsed = time - timeStarted.value;
      fun({
        delta,
        stop,
        time,
        elapsed
      });

      if (stopped.value) {
        stopped.value = false;
        return;
      }
      id.value = (requestAnimationFrame((time) => loop(time / 1000)));
    };
    requestAnimationFrame((time) => loop(time / 1000));
    return stop;
  };

  // Stop the rAF loop when the owning scope disposes; otherwise a callback
  // firing during unmount can crash Vue while it unmounts a <Teleport>
  // subtree (vuejs/core#12702).
  if (getCurrentScope()) onScopeDispose(stop);

  return {
    run,
    stop,
    running
  }
};
